/**
 * The "Picture sharing" card (Community Assets): the app owner's switch for
 * letting the app's users share the pictures they make with each other — a
 * shared book's illustrations, say — and what it costs.
 *
 * Turning it on is a billing commitment (the owner pays to store and serve
 * what is shared, daily), so it takes a second, explicit confirmation that
 * states the charges. Turning it off stops NEW shares only: what is already
 * shared keeps showing, and keeps being billed, until its authors remove it.
 *
 * Renders nothing for someone who is not the app's billing owner (the endpoint
 * 403s) and for an app that cannot share pictures — one whose manifest does not
 * ask for `assets:community:publish` and that has never had sharing on.
 */

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { CommunitySharingResponse } from '@/lib/api';
import { useCommunitySharing, useSetCommunitySharing } from '../hooks/useCommunitySharing';

function formatCredits(credits: number): string {
  return credits < 0.01 && credits > 0 ? '< 0.01' : credits.toFixed(2);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function PictureSharingSection({ archeId }: { archeId: string }) {
  const { data } = useCommunitySharing(archeId);
  if (!data) return null;
  const relevant = data.declaresSharing || data.settings.enabled || data.usage.sharedPictures > 0;
  if (!relevant) return null;
  return <PictureSharingCard archeId={archeId} data={data} />;
}

function PictureSharingCard({
  archeId,
  data,
}: {
  archeId: string;
  data: CommunitySharingResponse;
}) {
  const { settings, usage, rates } = data;
  const [confirming, setConfirming] = useState(false);
  const mutation = useSetCommunitySharing(archeId);
  const total = usage.storageCredits + usage.egressCredits;
  const hasBills = usage.chargedDays > 0 || usage.failedDays > 0;

  const change = (enabled: boolean) =>
    mutation.mutate(enabled, { onSuccess: () => setConfirming(false) });

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>Picture sharing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Lets people who use this app share the pictures they make with its other users — a shared
          book&apos;s illustrations, for example. Every picture is checked before anyone else sees
          it.
        </p>

        {settings.suspended && (
          <div
            role="alert"
            className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <span>
              New shares are paused: this app&apos;s picture-sharing charges failed for insufficient
              credits. Pictures already shared keep showing. Add credits and sharing resumes after
              the next daily charge.
            </span>
          </div>
        )}

        <div>
          <div className="text-sm font-medium">
            {settings.enabled
              ? settings.enabledAt
                ? `On since ${formatDate(settings.enabledAt)}`
                : 'On'
              : 'Off'}
          </div>
          {(usage.sharedPictures > 0 || usage.waitingPictures > 0) && (
            <div className="text-sm text-muted-foreground">
              {plural(usage.sharedPictures, 'picture', 'pictures')} shared
              {usage.waitingPictures > 0 &&
                ` · ${plural(usage.waitingPictures, 'picture', 'pictures')} waiting for a check`}
            </div>
          )}
        </div>

        {hasBills && (
          <div>
            <div className="text-sm font-medium">This month</div>
            <div className="text-sm text-muted-foreground">
              <span className="font-mono tabular-nums">{formatCredits(total)}</span> credits —
              storage{' '}
              <span className="font-mono tabular-nums">{formatCredits(usage.storageCredits)}</span>,
              serving{' '}
              <span className="font-mono tabular-nums">{formatCredits(usage.egressCredits)}</span> ·{' '}
              {plural(usage.chargedDays, 'day', 'days')} charged
              {usage.failedDays > 0 && ` · ${usage.failedDays} failed`}
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          You pay to store and serve what is shared: {rates.storageCreditsPerGbMonth} credits per
          GB-month stored and {rates.egressCreditsPerGb} credits per GB served, charged daily for
          the day before. Each picture&apos;s safety check ({rates.moderation.creditsPerImage}{' '}
          credits) is paid by the person who shares it.
        </p>

        {confirming ? (
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm">
              {settings.enabled
                ? 'New shares stop. Pictures already shared keep showing, and you keep paying for their storage and serving until their authors remove them.'
                : 'People using this app will be able to share pictures with its other users, and you will be billed daily for storing and serving them.'}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant={settings.enabled ? 'destructive' : 'default'}
                disabled={mutation.isPending}
                onClick={() => change(!settings.enabled)}
              >
                {settings.enabled ? 'Turn off picture sharing' : 'Turn on and accept the charges'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={mutation.isPending}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            size="sm"
            variant={settings.enabled ? 'outline' : 'default'}
            onClick={() => {
              mutation.reset();
              setConfirming(true);
            }}
          >
            {settings.enabled ? 'Turn off…' : 'Turn on picture sharing…'}
          </Button>
        )}
        {mutation.isError && (
          <p className="text-sm text-destructive">Could not change picture sharing. Try again.</p>
        )}
      </CardContent>
    </Card>
  );
}
